/* eslint-disable max-params */
import type { RouteLocationNormalized } from 'vue-router'

import { ROUTE_AURA } from '~/contexts/aura/router/routes'
import type { RoutesWithThreadIdParam } from '~/contexts/aura/router/types'
import { navigationErrorHandler } from '~/contexts/dm/router/navigation-error-handler'

import { Navigation404Error } from '~/router/errors'
import { hasParamsChanged } from '~/router/utils/has-params-changed'

import { verifyFeatureAvailability } from '~/features-generic/config-manager/composables/feature-manager'

import type { NavigationGuard } from '~/typings/vue-router.d'

/**
 * Check the given community exists
 * @param containerUri the community
 * @throws Navigation404Error if the conversation does not exist
 */
async function checkAuraConversationExists(containerUri: string): Promise<void> {
  try {
    const { useConversationsAURAStore } = await import('~/features-swym/aura/stores/conversations-aura')

    const storeConversations = useConversationsAURAStore()
    await storeConversations.fetchConversation(containerUri)
  }
  catch {
    // this conversation do not exist..
    throw new Navigation404Error('This conversation doesn\'t exist', { name: ROUTE_AURA.HOME, replace: true })
  }
}

/**
 * Control access to the aura context
 */
export const navigationGuardAura: NavigationGuard<ROUTE_AURA> = (to, from, next, isMounted, push) => {
  return Promise.resolve()
    .then(async () => {
      return verifyFeatureAvailability('ai.aura.enable')
        .then((result) => {
          if (!result)
            throw new Navigation404Error('This page does not exist')
        })
    })
    .then(async () => {
      if (hasParamsChanged(to, from, 'thread_id')) {
        const _to = to as RouteLocationNormalized<RoutesWithThreadIdParam>
        if (!unref(isMounted)) {
          checkAuraConversationExists(_to.params.thread_id)
            .catch((error) => {
              navigationErrorHandler(error, _to, from)
              push(error.to)
            })
        }
        else {
          return checkAuraConversationExists(_to.params.thread_id)
        }
      }
    })
    .then(() => next())
    .catch(error => next(error))
}
